/**
 * Audit log admin page.
 *
 * Layout:
 *   PageHeader → HeroPanel (action-category buckets)
 *              → Toolbar (action search + actor / kind / severity chips)
 *              → Vertical timeline grouped by hour with markers
 *
 * Per-entry features:
 *   - actor avatar (deterministic gradient hue from actor_id)
 *   - severity tone derived from action prefix
 *   - diff viewer when metadata_json contains before/after
 *   - "Copy link" → URL with #audit-{id} anchor
 *
 * The AuditLogRow shape doesn't carry a real "severity" field, so we
 * synthesize it from the action prefix:
 *   delete_/revoke_/suspend_/yank_   → critical
 *   disable_/deactivate_             → warning
 *   update_/change_/patch_/edit_     → degraded
 *   create_/issue_/resume_/enable_   → healthy
 *   else                             → neutral
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  ScrollText, RefreshCw, Search, ClipboardCopy, Check,
  ShieldX, Pencil, Plus, FileQuestion,
} from 'lucide-react';
import { AdminApiClient, type AuditLogRow } from '../api';
import { failureFrom, type Failure } from '../lib/api-state';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { FetchState } from '../components/ui/FetchState';

type Severity = 'critical' | 'warning' | 'degraded' | 'healthy' | 'neutral';

function severityFromAction(action: string): Severity {
  if (/^(delete_|revoke_|suspend_|yank_)/.test(action)) return 'critical';
  if (/^(disable_|deactivate_)/.test(action)) return 'warning';
  if (/^(update_|change_|patch_|edit_)/.test(action)) return 'degraded';
  if (/^(create_|issue_|resume_|enable_|approve_)/.test(action)) return 'healthy';
  return 'neutral';
}

const SEVERITY_TONES: Record<Severity, string> = {
  critical: '#ef4444',
  warning:  '#fb923c',
  degraded: '#fbbf24',
  healthy:  '#34d399',
  neutral:  '#94a3b8',
};

function bandFromSeverity(s: Severity): Band {
  switch (s) {
    case 'critical': return 'critical';
    case 'warning':  return 'warning';
    case 'degraded': return 'degraded';
    case 'healthy':  return 'healthy';
    case 'neutral':  return 'neutral';
  }
}

export function AuditLogPage(): JSX.Element {
  const { state } = useSession();
  const [rows, setRows] = useState<AuditLogRow[] | null>(null);
  const [actionQuery, setActionQuery] = useState('');
  const [actorFilter, setActorFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<Severity | 'all'>('all');
  const [err, setErr] = useState<Failure | null>(null);
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setRefreshing(true);
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    api.auditLogs({ limit: 200 })
      .then((r) => { setRows(r.audit_logs); setErr(null); })
      .catch((e) => setErr(failureFrom(e, 'the audit log')))
      .finally(() => setRefreshing(false));
  }, [state.token, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  // Open the entry from the URL hash on first load (if present).
  useEffect(() => {
    if (!rows) return;
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (hash && hash.startsWith('#audit-')) {
      requestAnimationFrame(() => {
        const el = document.getElementById(hash.slice(1));
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [rows]);

  const counts = useMemo(() => {
    const c = { all: 0, critical: 0, warning: 0, degraded: 0, healthy: 0, neutral: 0 };
    for (const r of rows ?? []) {
      c.all += 1;
      c[severityFromAction(r.action)] += 1;
    }
    return c;
  }, [rows]);

  const actorTypes = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows ?? []) s.add(r.actor_type);
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = actionQuery.trim().toLowerCase();
    return (rows ?? []).filter((r) => {
      if (q && !r.action.toLowerCase().includes(q)) return false;
      if (actorFilter !== 'all' && r.actor_type !== actorFilter) return false;
      if (severityFilter !== 'all' && severityFromAction(r.action) !== severityFilter) return false;
      return true;
    });
  }, [rows, actionQuery, actorFilter, severityFilter]);

  // Group by hour for timeline markers.
  const hours = useMemo(() => {
    const buckets = new Map<string, AuditLogRow[]>();
    for (const r of filtered) {
      const d = new Date(r.created_at);
      const key = `${d.getUTCFullYear()}-${(d.getUTCMonth() + 1).toString().padStart(2, '0')}-${d.getUTCDate().toString().padStart(2, '0')}T${d.getUTCHours().toString().padStart(2, '0')}`;
      let arr = buckets.get(key);
      if (!arr) { arr = []; buckets.set(key, arr); }
      arr.push(r);
    }
    return Array.from(buckets.entries())
      .sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  return (
    <div className="page">
      <PageHeader
        title="Audit log"
        subtitle="Every staff mutation, auto-provisioning event, and cross-region forward — last 200."
        icon={ScrollText}
        accent="#94a3b8"
        actions={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      {rows && <HeroPanel
        icon={ScrollText}
        accent="#94a3b8"
        title="Activity buckets"
        subtitle="Action categories, derived from action-name prefix."
        kpis={[
          <HeroKpi key="t" label="Total" value={counts.all} hint="visible window" />,
          <HeroKpi key="c" label="Destructive" value={counts.critical}
                   band={counts.critical > 0 ? 'critical' : 'neutral'}
                   hint="delete · revoke · suspend · yank" />,
          <HeroKpi key="m" label="Mutations" value={counts.degraded + counts.warning}
                   band={(counts.degraded + counts.warning) > 0 ? 'degraded' : 'neutral'}
                   hint="update · change · disable" />,
          <HeroKpi key="g" label="Provisioning" value={counts.healthy}
                   band={counts.healthy > 0 ? 'healthy' : 'neutral'}
                   hint="create · issue · resume · enable" />,
          <HeroKpi key="o" label="Other" value={counts.neutral}
                   band="neutral" />,
        ]}
      />}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Toolbar
          query={actionQuery}
          onQuery={setActionQuery}
          actorTypes={actorTypes}
          actorFilter={actorFilter}
          onActorFilter={setActorFilter}
          severityFilter={severityFilter}
          onSeverityFilter={setSeverityFilter}
          counts={counts}
        />

        <FetchState
          loading={!rows && !err}
          error={err}
          empty={!!rows && filtered.length === 0}
          emptyTitle="No audit entries match"
          emptyDescription="Loosen the filters or refresh."
          onRetry={onRefresh}
        >
          <Timeline hours={hours} />
        </FetchState>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Toolbar
 * ────────────────────────────────────────────────────────────────── */

function Toolbar({
  query, onQuery,
  actorTypes, actorFilter, onActorFilter,
  severityFilter, onSeverityFilter, counts,
}: {
  query: string;
  onQuery: (q: string) => void;
  actorTypes: string[];
  actorFilter: string;
  onActorFilter: (a: string) => void;
  severityFilter: Severity | 'all';
  onSeverityFilter: (s: Severity | 'all') => void;
  counts: Record<Severity | 'all', number>;
}): JSX.Element {
  const sevChips: Array<{ key: Severity | 'all'; label: string; count: number }> = [
    { key: 'all',      label: 'All',           count: counts.all },
    { key: 'critical', label: 'Destructive',   count: counts.critical },
    { key: 'warning',  label: 'Disable',       count: counts.warning },
    { key: 'degraded', label: 'Update',        count: counts.degraded },
    { key: 'healthy',  label: 'Provision',     count: counts.healthy },
    { key: 'neutral',  label: 'Other',         count: counts.neutral },
  ];
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <SearchInput value={query} onChange={onQuery} placeholder="Filter action…" />
        <select
          value={actorFilter}
          onChange={(e) => onActorFilter(e.target.value)}
          style={{
            background: 'var(--bg-overlay)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
            padding: '7px 10px',
            borderRadius: 8,
            fontSize: '0.8125rem',
          }}
        >
          <option value="all">All actors</option>
          {actorTypes.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>
      <ChipRow chips={sevChips} value={severityFilter} onChange={onSeverityFilter} />
    </div>
  );
}

function SearchInput({
  value, onChange, placeholder,
}: { value: string; onChange: (v: string) => void; placeholder?: string }): JSX.Element {
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <Search
        width={14} height={14}
        style={{ position: 'absolute', left: 10, color: 'var(--text-muted)', pointerEvents: 'none' }}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: '7px 10px 7px 30px',
          background: 'var(--bg-overlay)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          color: 'var(--text-primary)',
          fontSize: '0.8125rem',
          minWidth: 220,
          outline: 'none',
        }}
      />
    </div>
  );
}

function ChipRow<T extends string>({
  chips, value, onChange,
}: {
  chips: Array<{ key: T; label: string; count: number }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filters"
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-overlay)',
        flexWrap: 'wrap', width: 'fit-content',
      }}
    >
      {chips.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            aria-pressed={active}
            onClick={() => onChange(c.key)}
            style={{
              display: 'inline-flex', gap: 5, alignItems: 'center',
              padding: '5px 9px', border: 'none', borderRadius: 8,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
            }}
          >
            {c.label}
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.6875rem',
                color: 'var(--text-muted)',
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                padding: '0 6px',
                borderRadius: 999,
                minWidth: 18, textAlign: 'center',
              }}
            >{c.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Timeline grouped by hour
 * ────────────────────────────────────────────────────────────────── */

function Timeline({ hours }: { hours: Array<[string, AuditLogRow[]]> }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {hours.map(([hourKey, entries]) => (
        <HourGroup key={hourKey} hourKey={hourKey} entries={entries} />
      ))}
    </div>
  );
}

function HourGroup({
  hourKey, entries,
}: { hourKey: string; entries: AuditLogRow[] }): JSX.Element {
  // hourKey e.g. "2026-04-28T15"
  const [date, hour] = hourKey.split('T');
  const label = `${date} · ${hour}:00 UTC`;

  return (
    <section>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          marginBottom: 10,
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}
      >
        <span
          aria-hidden
          style={{
            display: 'inline-block', width: 6, height: 6,
            borderRadius: 999, background: 'var(--accent)',
          }}
        />
        {label}
        <span aria-hidden style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
          {entries.length} event{entries.length === 1 ? '' : 's'}
        </span>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.map((r) => <Entry key={r.id} row={r} />)}
      </ul>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Single audit entry (avatar + action + diff + copy link)
 * ────────────────────────────────────────────────────────────────── */

function Entry({ row }: { row: AuditLogRow }): JSX.Element {
  const sev = severityFromAction(row.action);
  const tone = SEVERITY_TONES[sev];
  const Icon = iconForAction(row.action);
  const [copied, setCopied] = useState(false);

  const before = (row.metadata_json && typeof row.metadata_json === 'object')
    ? (row.metadata_json as Record<string, unknown>).before
    : undefined;
  const after = (row.metadata_json && typeof row.metadata_json === 'object')
    ? (row.metadata_json as Record<string, unknown>).after
    : undefined;
  const hasDiff = before !== undefined && after !== undefined;

  const copyLink = async (): Promise<void> => {
    const url = `${window.location.origin}${window.location.pathname}#audit-${row.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // best-effort
    }
  };

  return (
    <li
      id={`audit-${row.id}`}
      style={{
        display: 'flex',
        gap: 10,
        padding: 12,
        borderRadius: 10,
        background: 'var(--bg-raised)',
        border: '1px solid var(--border)',
        transition: 'border-color 120ms ease',
      }}
    >
      <ActorAvatar row={row} tone={tone} icon={Icon} />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <code
            style={{
              fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)',
              fontFamily: 'inherit',
            }}
          >
            {row.action}
          </code>
          <StatusChip band={bandFromSeverity(sev)} size="sm" noDot>
            {labelForSeverity(sev)}
          </StatusChip>
          {row.target_type && row.target_id && (
            <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
              <span style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>{row.target_type}</span>{' '}
              <code>{row.target_id.slice(0, 12)}…</code>
            </span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
            {fmtTime(row.created_at)}
            {row.ip && <> · <code>{row.ip}</code></>}
          </span>
        </div>

        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          <span>{row.actor_type}</span>
          {row.actor_id && (
            <> · <code>{row.actor_id.slice(0, 12)}…</code></>
          )}
          {row.reason && <> · {row.reason}</>}
        </div>

        {hasDiff && <DiffViewer before={before} after={after} />}

        {!hasDiff && row.metadata_json && Object.keys(row.metadata_json).length > 0 && (
          <pre
            style={{
              margin: 0,
              padding: '6px 8px',
              borderRadius: 6,
              background: 'var(--bg-overlay)',
              border: '1px solid var(--border)',
              fontSize: '0.6875rem',
              fontFamily: 'ui-monospace, monospace',
              color: 'var(--text-muted)',
              whiteSpace: 'pre-wrap',
              maxHeight: 100,
              overflow: 'auto',
            }}
          >
            {JSON.stringify(row.metadata_json, null, 2)}
          </pre>
        )}
      </div>

      <button
        type="button"
        onClick={() => void copyLink()}
        title="Copy link to this event"
        aria-label={copied ? 'Link copied' : 'Copy link to this audit event'}
        style={{
          flex: 'none',
          background: 'transparent',
          border: 'none',
          padding: 6,
          borderRadius: 6,
          cursor: 'pointer',
          color: copied ? 'var(--success)' : 'var(--text-muted)',
          alignSelf: 'flex-start',
        }}
      >
        {copied ? <Check width={14} height={14} /> : <ClipboardCopy width={14} height={14} />}
      </button>
    </li>
  );
}

function ActorAvatar({
  row, tone, icon: Icon,
}: {
  row: AuditLogRow;
  tone: string;
  icon: typeof ScrollText;
}): JSX.Element {
  const seed = row.actor_id ?? row.actor_type;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  const h1 = Math.abs(hash) % 360;
  const h2 = (h1 + 60) % 360;
  return (
    <div
      aria-hidden
      style={{
        flex: 'none',
        width: 32, height: 32,
        borderRadius: 999,
        position: 'relative',
        background: `linear-gradient(135deg, hsl(${h1} 70% 55%), hsl(${h2} 70% 45%))`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#0b0f19',
      }}
    >
      <Icon width={14} height={14} />
      <span
        style={{
          position: 'absolute',
          right: -2, bottom: -2,
          width: 12, height: 12,
          borderRadius: 999,
          background: tone,
          border: '2px solid var(--bg-raised)',
        }}
      />
    </div>
  );
}

function iconForAction(action: string): typeof ScrollText {
  if (/^(delete_|revoke_|suspend_|yank_)/.test(action)) return ShieldX;
  if (/^(disable_|deactivate_)/.test(action)) return ShieldX;
  if (/^(update_|change_|patch_|edit_)/.test(action)) return Pencil;
  if (/^(create_|issue_|resume_|enable_|approve_)/.test(action)) return Plus;
  return FileQuestion;
}

function labelForSeverity(s: Severity): string {
  switch (s) {
    case 'critical': return 'destructive';
    case 'warning':  return 'disable';
    case 'degraded': return 'update';
    case 'healthy':  return 'provision';
    case 'neutral':  return 'event';
  }
}

/* ──────────────────────────────────────────────────────────────────
 * Diff viewer (before / after)
 * ────────────────────────────────────────────────────────────────── */

function DiffViewer({
  before, after,
}: { before: unknown; after: unknown }): JSX.Element {
  const beforeText = formatJson(before);
  const afterText = formatJson(after);
  return (
    <div
      style={{
        display: 'grid',
        gap: 6,
        gridTemplateColumns: '1fr 1fr',
      }}
    >
      <DiffPanel label="before" tone="rgba(239, 68, 68, 0.35)" body={beforeText} />
      <DiffPanel label="after"  tone="rgba(16, 185, 129, 0.35)" body={afterText} />
    </div>
  );
}

function DiffPanel({
  label, tone, body,
}: { label: string; tone: string; body: string }): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <pre
        style={{
          margin: 0,
          padding: '6px 8px',
          borderRadius: 6,
          background: 'var(--bg-overlay)',
          border: `1px solid ${tone}`,
          fontSize: '0.6875rem',
          fontFamily: 'ui-monospace, monospace',
          color: 'var(--text-primary)',
          whiteSpace: 'pre-wrap',
          maxHeight: 140,
          overflow: 'auto',
        }}
      >
        {body}
      </pre>
    </div>
  );
}

function formatJson(v: unknown): string {
  if (v == null) return 'null';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v, null, 2); }
  catch { return '(unrepresentable)'; }
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}:${d.getUTCSeconds().toString().padStart(2, '0')}Z`;
}

